import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, notFound, useRouter } from "@tanstack/react-router"
import { useMemo } from "react"
import * as z from "zod"

import type { ExtendedClient } from "@repo/supabase/database-types"
import { PagePanel } from "@repo/ui/components/ui/page-panel"
import { useIsMobile } from "@repo/ui/hooks/use-mobile"
import { cn } from "@repo/ui/lib/utils"
import { pick } from "@repo/utils/collections"
import { logger } from "@repo/utils/logger"
import { getExtendedBusinessEntitiesQueryOptions } from "~/modules/business-entities/business-entities.queries"
import { getExtendedClientsQueryOptions } from "~/modules/clients/clients.queries"
import { ClientsSearchList } from "~/modules/clients/components/clients-search-list"
import { ResourceInvoiceForm } from "~/modules/resources/components/resource-invoice-form"
import {
  ADMIN_EDITABLE_RESOURCE_INVOICE_STATUSES,
  type AdminEditableResourceInvoiceStatus,
} from "~/modules/resources/resource-invoices.constants"
import { getExtendedResourceInvoiceQueryOptions } from "~/modules/resources/resource-invoices.queries"

const log = logger.child({
  module: "resource-invoices-invoice-route",
})

const searchSchema = z.object({
  be_slug: z.string().optional(),
  client_slug: z.string().optional(),
})

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/resource-invoices/$invoiceId",
)({
  component: RouteComponent,
  preload: false,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => pick(search, ["be_slug", "client_slug"]),
  loader: async ({ context, params, deps }) => {
    const { invoiceId } = params

    context.queryClient
      .ensureQueryData(getExtendedResourceInvoiceQueryOptions({ invoiceId }))
      .catch((error) => {
        log.error("Error getting extended resource invoice", { error })
        return null
      })

    context.queryClient
      .ensureQueryData(getExtendedBusinessEntitiesQueryOptions())
      .catch((error) => {
        log.error("Error getting extended business entities", { error })
        return null
      })

    if (deps.be_slug) {
      context.queryClient
        .ensureQueryData(getExtendedClientsQueryOptions())
        .catch((error) => {
          log.error("Error getting extended clients", { error })
          return null
        })
    }

    return {
      invoiceId,
    }
  },
})

function RouteComponent() {
  const { invoiceId } = Route.useLoaderData()

  const { data: invoice } = useSuspenseQuery(
    getExtendedResourceInvoiceQueryOptions({ invoiceId }),
  )

  if (!invoice) {
    throw notFound()
  }

  const router = useRouter()

  const isMobile = useIsMobile()

  const { be_slug, client_slug } = Route.useSearch()

  const { data: businessEntitiesData, isLoading: isBusinessEntitiesLoading } =
    useQuery(getExtendedBusinessEntitiesQueryOptions())

  const selectedBusinessEntity = useMemo(() => {
    const selected = businessEntitiesData?.businessEntities.find(
      (entity) => entity.slug === be_slug,
    )

    if (selected) {
      return selected
    }

    return businessEntitiesData?.businessEntities[0]
  }, [businessEntitiesData, be_slug])

  const businessEntityFilterOptions = useMemo(() => {
    return (
      businessEntitiesData?.businessEntities.map((entity) => ({
        label: entity.name,
        value: entity.slug,
      })) ?? []
    )
  }, [businessEntitiesData])

  const { data: clientsData, isLoading: isClientsLoading } = useQuery({
    ...getExtendedClientsQueryOptions({
      filters: {
        page: 1,
        page_size: 1000, // TODO: This should be replaced with an infinite scroll list (SearchList needs to be updated to support this)
        columnFilters: selectedBusinessEntity?.unit_id
          ? [
              {
                id: "business_entity_unit_ids",
                value: [selectedBusinessEntity.unit_id],
              },
            ]
          : undefined,
      },
    }),
    enabled: !isBusinessEntitiesLoading,
  })

  const selectedClient = useMemo(() => {
    const selected = clientsData?.clients.find(
      (client) => client.slug === client_slug,
    )

    if (selected) {
      return selected
    }

    return null
  }, [clientsData, client_slug])

  const onSelectBusinessEntity = (businessEntitySlug: string | null) => {
    router.navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        be_slug: businessEntitySlug ?? undefined,
      }),
    })
  }

  const onSelectClient = (client: ExtendedClient | null) => {
    router.navigate({
      to: "/resource-invoices",
      search: (prev) => ({
        ...prev,
        client_slug: client?.slug ?? client_slug ?? undefined,
      }),
    })
  }

  const onBack = () => {
    router.navigate({
      to: "/resource-invoices",
      search: (prev) => ({
        ...prev,
        client_slug: client_slug,
        be_slug: be_slug,
      }),
    })
  }

  return (
    <PagePanel title={`Invoice #${invoice.invoice_number ?? ""}`}>
      <div className="flex h-full flex-col gap-4 md:flex-row">
        <div
          className={cn("block md:block md:w-1/3 lg:w-1/4", {
            hidden: isMobile,
          })}
        >
          <ClientsSearchList
            clients={clientsData?.clients ?? []}
            selectedClientSlug={selectedClient?.slug ?? null}
            onSelectClient={onSelectClient}
            filterOptions={businessEntityFilterOptions}
            filterValue={selectedBusinessEntity?.slug ?? null}
            setFilterValue={onSelectBusinessEntity}
            filterPlaceholder="Select a business entity"
            isLoading={isClientsLoading || isBusinessEntitiesLoading}
          />
        </div>

        <div className={cn("block flex-1 md:w-2/3 lg:w-3/4")}>
          <ResourceInvoiceForm
            key={invoiceId}
            onBack={onBack}
            invoice={invoice}
            isEditable={ADMIN_EDITABLE_RESOURCE_INVOICE_STATUSES.includes(
              invoice.status as AdminEditableResourceInvoiceStatus,
            )}
            isAdmin={true}
          />
        </div>
      </div>
    </PagePanel>
  )
}
